import { api } from './api';

// ============ Types ============

export interface Address {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
}

export interface LineItem {
    description: string;
    quantity: number;
    unitPrice: number;
    amount: number;
    taxable: boolean;
}

export interface TaxItem {
    name: string;
    rate: number;
    amount: number;
    taxId?: string;
}

export interface BillTo {
    companyName: string;
    contactName?: string;
    email: string;
    phone?: string;
    address?: Address;
    taxId?: string;
}

export interface BillFrom {
    businessName: string;
    ownerName?: string;
    email: string;
    phone?: string;
    address?: Address;
    taxId?: string;
    businessRegistration?: string;
}

export interface BankDetails {
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
    routingNumber?: string;
    swiftCode?: string;
    iban?: string;
}

export interface PaymentMethodInfo {
    method: string;
    details: string;
}

export interface Discount {
    amount: number;
    percentage?: number;
    reason?: string;
}

export interface Invoice {
    id: string;
    invoiceNumber: string;
    projectId: string;
    contractId?: string;
    milestoneId?: string;
    type: 'quotation' | 'proforma' | 'milestone' | 'final' | 'adhoc' | 'credit_note';
    billTo: BillTo;
    billFrom: BillFrom;
    lineItems: LineItem[];
    subtotal: number;
    taxes: TaxItem[];
    totalTax: number;
    discount: Discount;
    total: number;
    currency: string;
    exchangeRate: number;
    issueDate: string;
    dueDate: string;
    paymentTerms: string;
    paymentTermsDays: number;
    lateFeePercentage: number;
    status: 'draft' | 'sent' | 'viewed' | 'partial' | 'paid' | 'overdue' | 'cancelled' | 'refunded';
    amountPaid: number;
    amountDue: number;
    paidAt?: string;
    sentAt?: string;
    viewedAt?: string;
    remindersSent: number;
    lastReminderAt?: string;
    bankDetails?: BankDetails;
    paymentMethods: PaymentMethodInfo[];
    notes?: string;
    terms?: string;
    pdfUrl?: string;
    attachments: string[];
    createdAt: string;
    updatedAt: string;
}

export interface Payment {
    id: string;
    paymentNumber: string;
    invoiceId: string;
    projectId?: string;
    amount: number;
    currency: string;
    exchangeRate: number;
    paymentMethod: 'bank_transfer' | 'wire' | 'paypal' | 'stripe' | 'check' | 'cash' | 'credit_card' | 'other';
    transactionId?: string;
    transactionDate: string;
    bankAccount?: BankDetails;
    status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded' | 'cancelled';
    verified: boolean;
    verifiedBy?: string;
    verifiedAt?: string;
    notes?: string;
    internalNotes?: string;
    receiptId?: string;
    receiptSent: boolean;
    receiptSentAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface Receipt {
    id: string;
    receiptNumber: string;
    invoiceId: string;
    paymentId: string;
    projectId?: string;
    receiptDate: string;
    amountReceived: number;
    currency: string;
    paymentMethod: string;
    clientName: string;
    clientEmail: string;
    businessName: string;
    businessEmail?: string;
    pdfUrl?: string;
    sentAt?: string;
    sentTo: string[];
    createdAt: string;
    updatedAt: string;
}

export interface InvoiceStats {
    unpaid: { count: number; amount: number };
    pending: { count: number; amount: number };
    paid: { count: number; amount: number };
    overdue: { count: number; amount: number };
    totalRevenue: number;
    totalInvoices: number;
}

// ============ Billing Settings Types ============

export interface BusinessInfo {
    legalName: string;
    tradingName?: string;
    email: string;
    phone?: string;
    website?: string;
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    logo?: string;
}

export interface RegionalTaxRate {
    region: string;
    rate: number;
    name: string;
}

export interface TaxSettings {
    taxId?: string;
    taxIdType?: string;
    taxRegistered: boolean;
    defaultTaxRate: number;
    taxName: string;
    regionalTaxRates: RegionalTaxRate[];
}

export interface BankAccountSetting {
    accountName: string;
    bankName?: string;
    accountNumber?: string;
    routingNumber?: string;
    swiftCode?: string;
    iban?: string;
    isDefault: boolean;
}

export interface PaymentMethodSetting {
    method: string;
    details: string;
    isActive: boolean;
}

export interface InvoiceDefaults {
    prefix: string;
    nextNumber: number;
    paymentTerms: string;
    paymentTermsDays: number;
    lateFeePercentage: number;
    currency: string;
    notes?: string;
    terms?: string;
}

export interface MilestoneStep {
    name: string;
    percentage: number;
    description?: string;
    trigger?: string;
}

export interface MilestoneTemplate {
    name: string;
    milestones: MilestoneStep[];
}

export interface AutomationSettings {
    autoSendReceipts: boolean;
    autoReminders: boolean;
    reminderSchedule: Array<{ daysBefore: number; enabled: boolean }>;
}

export interface BillingSettings {
    id: string;
    teamId: string;
    businessInfo?: BusinessInfo;
    taxSettings: TaxSettings;
    bankAccounts: BankAccountSetting[];
    paymentMethods: PaymentMethodSetting[];
    invoiceDefaults: InvoiceDefaults;
    milestoneTemplates: MilestoneTemplate[];
    automation: AutomationSettings;
}

// ============ DTO Types ============

export interface CreateInvoiceDto {
    projectId?: string;
    contractId?: string;
    milestoneId?: string;
    type?: string;
    billTo: BillTo;
    billFrom: BillFrom;
    lineItems: LineItem[];
    taxes?: TaxItem[];
    discount?: Discount;
    currency?: string;
    exchangeRate?: number;
    issueDate?: string;
    dueDate?: string;
    paymentTerms?: string;
    paymentTermsDays?: number;
    bankDetails?: BankDetails;
    paymentMethods?: PaymentMethodInfo[];
    notes?: string;
    terms?: string;
}

export interface UpdateInvoiceDto {
    type?: string;
    billTo?: BillTo;
    billFrom?: BillFrom;
    lineItems?: LineItem[];
    taxes?: TaxItem[];
    discount?: Discount;
    dueDate?: string;
    paymentTerms?: string;
    bankDetails?: BankDetails;
    paymentMethods?: PaymentMethodInfo[];
    notes?: string;
    terms?: string;
    status?: string;
}

export interface CreatePaymentDto {
    invoiceId: string;
    amount: number;
    currency?: string;
    paymentMethod: string;
    transactionId?: string;
    transactionDate: string;
    bankAccount?: BankDetails;
    notes?: string;
    sendReceipt?: boolean;
}

export interface InvoiceFilters {
    status?: string;
    projectId?: string;
    search?: string;
    fromDate?: string;
    toDate?: string;
    page?: number;
    limit?: number;
}

// ============ API Service ============

const normalizeId = (data: any): any => {
    if (!data) return data;
    if (Array.isArray(data)) {
        return data.map(normalizeId);
    }
    if (typeof data === 'object') {
        const { _id, ...rest } = data;
        return { id: _id || data.id, ...rest };
    }
    return data;
};

export const billingService = {
    // ============ Invoices ============

    getInvoices: async (filters: InvoiceFilters = {}): Promise<{ invoices: Invoice[]; total: number }> => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined) params.append(key, String(value));
        });
        const response = await api.get(`/billing/invoices?${params.toString()}`);
        return {
            invoices: normalizeId(response.data.invoices),
            total: response.data.total,
        };
    },

    getInvoiceStats: async (): Promise<InvoiceStats> => {
        const response = await api.get('/billing/invoices/stats');
        return response.data;
    },

    getInvoice: async (id: string): Promise<Invoice> => {
        const response = await api.get(`/billing/invoices/${id}`);
        return normalizeId(response.data);
    },

    createInvoice: async (data: CreateInvoiceDto): Promise<Invoice> => {
        const response = await api.post('/billing/invoices', data);
        return normalizeId(response.data);
    },

    updateInvoice: async (id: string, data: UpdateInvoiceDto): Promise<Invoice> => {
        const response = await api.patch(`/billing/invoices/${id}`, data);
        return normalizeId(response.data);
    },

    deleteInvoice: async (id: string): Promise<void> => {
        await api.delete(`/billing/invoices/${id}`);
    },

    sendInvoice: async (id: string): Promise<Invoice> => {
        const response = await api.post(`/billing/invoices/${id}/send`);
        return normalizeId(response.data);
    },

    sendReminder: async (id: string): Promise<Invoice> => {
        const response = await api.post(`/billing/invoices/${id}/remind`);
        return normalizeId(response.data);
    },

    // ============ Payments ============

    getPayments: async (invoiceId?: string): Promise<Payment[]> => {
        const url = invoiceId ? `/billing/payments?invoiceId=${invoiceId}` : '/billing/payments';
        const response = await api.get(url);
        return normalizeId(response.data);
    },

    recordPayment: async (data: CreatePaymentDto): Promise<Payment> => {
        const response = await api.post('/billing/payments', data);
        return normalizeId(response.data);
    },

    updatePayment: async (id: string, data: Partial<Payment>): Promise<Payment> => {
        const response = await api.patch(`/billing/payments/${id}`, data);
        return normalizeId(response.data);
    },

    // ============ Receipts ============

    getReceipts: async (invoiceId?: string): Promise<Receipt[]> => {
        const url = invoiceId ? `/billing/receipts?invoiceId=${invoiceId}` : '/billing/receipts';
        const response = await api.get(url);
        return normalizeId(response.data);
    },

    getReceipt: async (id: string): Promise<Receipt> => {
        const response = await api.get(`/billing/receipts/${id}`);
        return normalizeId(response.data);
    },

    sendReceipt: async (id: string): Promise<Receipt> => {
        const response = await api.post(`/billing/receipts/${id}/send`);
        return normalizeId(response.data);
    },

    // ============ Settings ============

    getSettings: async (): Promise<BillingSettings> => {
        const response = await api.get('/billing/settings');
        return normalizeId(response.data);
    },

    updateSettings: async (data: Partial<BillingSettings>): Promise<BillingSettings> => {
        const response = await api.patch('/billing/settings', data);
        return normalizeId(response.data);
    },
};

export default billingService;
